import React, { useState } from "react";

function Bmi() {
  const [inputs, setInputs] = useState({
    weight: "",
    htf: "",
    hti: ""
  });
  const [results, setResults] = useState({
    answer: "",
    comment: ""
  });
  const [errors, setErrors] = useState({
    weight: "",
    htf: "",
    hti: ""
  });

  const validateField = (name, value) => {
    let error = "";
    
    if (name === "weight") {
      if (!value) {
        error = "Weight is required";
      } else if (!/^\d*(\.\d{0,1})?$/.test(value)) {
        error = "You can enter up to only one decimal place";
      } else if (parseFloat(value) < 2) {
        error = "Weight must be at least 2 kg";
      } else if (parseFloat(value) > 700) {
        error = "Weight must be less than 700 kg";
      }
    } else if (name === "htf") {
      if (!value) {
        error = "Feet is required";
      } else if (!/^\d+$/.test(value)) {
        error = "Feet must be a whole number";
      } else if (parseInt(value) < 1) {
        error = "Feet must be at least 1";
      } else if (parseInt(value) > 9) {
        error = "Feet must be 9 or less";
      }
    } else if (name === "hti") {
      if (value === "") {
        error = "Inches is required";
      } else if (!/^\d+$/.test(value)) {
        error = "Inches must be a whole number";
      } else if (parseInt(value) < 0) {
        error = "Inches cannot be negative";
      } else if (parseInt(value) >= 12) {
        error = "Inches must be less than 12";
      }
    }
    
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Different validation patterns for different fields
    let pattern;
    if (name === "weight") {
      pattern = /^\d*(\.\d{0,1})?$/; // Allow numbers and up to one decimal point for weight
    } else {
      pattern = /^\d*$/; // Only allow whole numbers for feet and inches
    }
    
    // For weight field, check if the new value has more than one decimal place
    if (name === "weight" && value !== "" && !pattern.test(value)) {
      setErrors({
        ...errors,
        weight: "You can enter up to only one decimal place"
      });
      return;
    }
    
    // For other fields or valid weight input
    if (value !== "" && !pattern.test(value)) {
      return;
    }
    
    setInputs({
      ...inputs,
      [name]: value
    });
    
    // Validate field in real-time
    const error = validateField(name, value);
    setErrors({
      ...errors,
      [name]: error
    });
  };

  const validateInputs = () => {
    const newErrors = {
      weight: validateField("weight", inputs.weight),
      htf: validateField("htf", inputs.htf),
      hti: validateField("hti", inputs.hti)
    };
    
    setErrors(newErrors);
    
    return !newErrors.weight && !newErrors.htf && !newErrors.hti;
  };

  const BMICalculate = () => {
    // Reset results
    setResults({ answer: "", comment: "" });
    
    if (!validateInputs()) {
      return;
    }

    const weightKg = parseFloat(inputs.weight);
    const heightFeet = parseInt(inputs.htf);
    const heightInches = parseInt(inputs.hti);

    // Convert height to meters
    const totalInches = (heightFeet * 12) + heightInches;
    const heightMeters = totalInches * 0.0254;
    
    // Calculate BMI using WHO/CDC standard formula: weight(kg) / height(m)^2
    const bmiValue = weightKg / (heightMeters * heightMeters);
    const displaybmi = bmiValue.toFixed(1);
    
    let comment = "";
    if (bmiValue < 18.5) comment = "Underweight";
    else if (bmiValue >= 18.5 && bmiValue < 25) comment = "Normal weight";
    else if (bmiValue >= 25 && bmiValue < 30) comment = "Overweight";
    else if (bmiValue >= 30 && bmiValue < 35) comment = "Obesity (Class I)";
    else if (bmiValue >= 35 && bmiValue < 40) comment = "Obesity (Class II)";
    else if (bmiValue >= 40) comment = "Extreme Obesity (Class III)";

    setResults({
      answer: displaybmi,
      comment: comment
    });
  };

  return (
    <div className="container mt-2">
      <div className="bmi">
        <div id="bmi_tab">
          <div className="row">
            <div className="col-md-6">
              <h3>What is BMI?</h3>
              <p style={{ fontSize: 16, textAlign: "justify" }}>
                BMI or Body Mass Index is a measure of body fat based on height
                and weight that applies to both adult men and women. Calculating
                the BMI is one of the best methods for the population assessment
                of overweight and obesity. It is low-cost and easy to use for
                clinicians and the general public. The use of BMI allows people
                to compare their own weight status to the general population.
                The only information required to calculate a person's BMI are
                height, weight, and the BMI formula. Any person with a BMI over
                25 would be classified as overweight.
              </p>
              
              <div className="clearfix">
                <br />
              </div>
              
              <h3>BMI Charts</h3>
              
              <h4 className="mt-4">Adults (18 years & older)</h4>
              <table className="table table-responsive table-bordered table-hover">
                <thead>
                  <tr style={{ backgroundColor: "#2c7bb6", color: "white" }}>
                    <th>Category</th>
                    <th>BMI Range (kg/m²)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                    <td>Underweight</td>
                    <td>&lt; 18.5</td>
                  </tr>
                  <tr style={{ backgroundColor: "#e6f3ff" }}>
                    <td>Normal weight</td>
                    <td>18.5 – 24.9</td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                    <td>Overweight</td>
                    <td>25.0 – 29.9</td>
                  </tr>
                  <tr style={{ backgroundColor: "#e6f3ff" }}>
                    <td>Obesity (Class I)</td>
                    <td>30.0 – 34.9</td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                    <td>Obesity (Class II)</td>
                    <td>35.0 – 39.9</td>
                  </tr>
                  <tr style={{ backgroundColor: "#e6f3ff" }}>
                    <td>Extreme Obesity (Class III)</td>
                    <td>≥ 40.0</td>
                  </tr>
                </tbody>
              </table>
              
              <h4 className="mt-4">Teens (2–19 years)</h4>
              <table className="table table-responsive table-bordered table-hover">
                <thead>
                  <tr style={{ backgroundColor: "#2c7bb6", color: "white" }}>
                    <th>Category</th>
                    <th>Percentile Range</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                    <td>Underweight</td>
                    <td>&lt; 5th percentile</td>
                  </tr>
                  <tr style={{ backgroundColor: "#e6f3ff" }}>
                    <td>Normal weight</td>
                    <td>5th – &lt; 85th percentile</td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                    <td>Overweight</td>
                    <td>85th – &lt; 95th percentile</td>
                  </tr>
                  <tr style={{ backgroundColor: "#e6f3ff" }}>
                    <td>Obese</td>
                    <td>≥ 95th percentile</td>
                  </tr>
                </tbody>
              </table>
              
             
            </div>
            
            <div className="col-md-6">
              <div style={{ width: "100%" }}>
                <div className="body-fat-ratio-heading bg-primary text-white p-2 rounded">
                  <h5 className="mb-0">Body Mass Index (BMI) Calculator</h5>
                </div>

                <div style={{ backgroundColor: "#F7F7F7" }} className="p-3">
                  <form>
                    <div className="row">
                      <div className="col-lg-12">
                        <label className="control-label mb-2 fw-bold" htmlFor="weight">
                          Weight In Kg*
                        </label>
                        <input
                          className={`form-control mb-0 ${errors.weight ? 'is-invalid' : ''}`}
                          id="weight"
                          name="weight"
                          placeholder="2-700"
                          type="text"
                          value={inputs.weight}
                          onChange={handleInputChange}
                        />
                        {errors.weight && <div className="invalid-feedback d-block" style={{fontSize:"11px",}}>{errors.weight}</div>}
                      </div>
                      
                      <div className="col-lg-12 mt-3">
                         <label className="control-label mb-0 fw-bold" htmlFor="height">Height*</label>
                        
                        <hr style={{ borderTop: "1px solid #ddd" }} />
                      </div>
                      
                      <div className="col-lg-12">
                        <label className="control-label mb-2" htmlFor="feets">
                          Feet*
                        </label>
                        <input
                          className={`form-control mb-0 ${errors.htf ? 'is-invalid' : ''}`}
                          id="feets"
                          name="htf"
                          placeholder="1-9"
                          type="text"
                          value={inputs.htf}
                          onChange={handleInputChange}
                        />
                        {errors.htf && <div className="invalid-feedback d-block">{errors.htf}</div>}
                      </div>
                      
                      <div className="col-lg-12 mt-3">
                        <label className="control-label mb-2" htmlFor="inches">
                          Inches*
                        </label>
                        <input
                          className={`form-control mb-0 ${errors.hti ? 'is-invalid' : ''}`}
                          id="inches"
                          name="hti"
                          placeholder="0-11"
                          type="text"
                          value={inputs.hti}
                          onChange={handleInputChange}
                        />
                        {errors.hti && <div className="invalid-feedback d-block">{errors.hti}</div>}
                      </div>
                      
                      <div className="col-lg-12 my-4 text-center">
                        <button
                          type="button"
                          onClick={BMICalculate}
                          className="btn btn-default"
                        >
                          Calculate BMI
                        </button>
                      </div>
                      
                      <div className="col-lg-12">
                        <p className="mb-2">
                          Based on international BMI classifications (WHO/CDC), your category is:
                        </p>
                        <div className="mb-3">
                          <input
                            className="form-control fw-bold text-center"
                            name="comment"
                            placeholder="Category"
                            type="text"
                            value={results.comment}
                            readOnly
                            style={{ fontSize: "15px" }}
                          />
                        </div>
                      </div>
                      
                      <div className="col-lg-12 mb-4">
                        <label className="control-label mb-2" htmlFor="bodyfat">
                          Body Mass Index (BMI)
                        </label>
                        <input
                          className="form-control fw-bold text-center"
                          id="bodyfat"
                          name="answer"
                          placeholder="Result"
                          type="text"
                          value={results.answer}
                          readOnly
                          style={{ fontSize: "16px", color: "#2c7bb6" }}
                        />
                      </div>
                    </div>
                  </form>
                </div>
                 <div className="card bg-light mt-3">
                <div className="card-body">
                  <h5 className="card-title">ℹ️ Note:</h5>
                  <p className="mb-0 text-justify">
                    For children and teens, BMI is compared using percentiles, not fixed numbers like adults. 
                    A percentile shows a child's position among others of the same age and sex.
                    For example, if a 12-year-old girl is in the 70th percentile, her BMI is higher than 70% of girls her age, and lower than 30%.
                    Percentiles are used because children grow at different rates, and body fat changes with age.
                  </p>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Bmi;